import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Registro dos `@id` já exibidos na página de notícia.
 *
 * "Mais lidas" não pode repetir o que o leitor já tem na tela: nem a notícia
 * aberta, nem os destaques da coluna lateral, nem os cards do "Veja também".
 * Os dois últimos só se conhecem depois que os respectivos componentes
 * buscaram seus dados, então cada um registra aqui o que renderizou e a lista
 * de mais lidas filtra reativamente.
 */

interface NewsExclusionsValue {
  /** `@id` (já em caminho de app) que não devem reaparecer. */
  excluded: Set<string>;
  /** Registra os ids de uma fonte. Substitui o registro anterior daquela fonte. */
  register: (source: string, ids: Array<string | undefined>) => void;
}

const NewsExclusionsContext = createContext<NewsExclusionsValue>({
  excluded: new Set(),
  register: () => {},
});

export const NewsExclusionsProvider: React.FC<{
  /** `@id` da notícia aberta — sempre excluída. */
  currentId?: string;
  children: React.ReactNode;
}> = ({ currentId, children }) => {
  const [sources, setSources] = useState<Record<string, string[]>>({});

  const register = useCallback(
    (source: string, ids: Array<string | undefined>) => {
      const next = ids.filter((id): id is string => Boolean(id));
      setSources((prev) => {
        const current = prev[source];
        // Sem esta guarda o setState dispara a cada render do componente que
        // registra, e o par register/render vira laço infinito.
        if (
          current &&
          current.length === next.length &&
          current.every((id, index) => id === next[index])
        ) {
          return prev;
        }
        return { ...prev, [source]: next };
      });
    },
    [],
  );

  const value = useMemo(() => {
    const excluded = new Set<string>();
    if (currentId) excluded.add(currentId);
    Object.values(sources).forEach((ids) =>
      ids.forEach((id) => excluded.add(id)),
    );
    return { excluded, register };
  }, [currentId, sources, register]);

  return (
    <NewsExclusionsContext.Provider value={value}>
      {children}
    </NewsExclusionsContext.Provider>
  );
};

export const useNewsExclusions = (): NewsExclusionsValue =>
  useContext(NewsExclusionsContext);

/**
 * Açúcar para quem só publica ids: registra `ids` sob `source` sempre que a
 * lista mudar.
 */
export const useRegisterExclusions = (
  source: string,
  ids: Array<string | undefined>,
): void => {
  const { register } = useNewsExclusions();
  const key = ids.filter(Boolean).join('|');
  React.useEffect(() => {
    register(source, ids);
    // `key` resume a lista: sem ele o array novo a cada render redispararia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, key, register]);
};

export default NewsExclusionsContext;
